/* eslint-disable react-refresh/only-export-components */
import { type ReactElement } from 'react'
import {
  Home,
  Search,
  Settings,
  AlertTriangle,
  User,
  LogOut,
  LogIn,
  PenTool,
  Briefcase,
  Users,
  BookOpen,
} from 'lucide-react'

export type NavItem = {
  id: string
  titleKey: string
  to: string
  icon: (props: React.SVGProps<SVGSVGElement>) => ReactElement
  variant?: 'white' | 'ghost' | 'link'
  /**
   * @deprecated Use requiredPermissions instead. Kept for backward compatibility.
   */
  /**
   * @deprecated Use requiredPermissions instead. Kept for backward compatibility.
   */
  adminOnly: boolean
  /**
   * Required permissions for this nav item (OR logic if multiple).
   * If undefined, no permission check is performed (public access).
   * Super admins bypass all permission checks.
   */
  requiredPermissions?: string[]
  requiresCrisis?: boolean
  alwaysShow?: boolean
}

// Central place for app constants. Add more as needed.
export const NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    titleKey: 'dashboard.titel',
    to: '/dashboard',
    icon: (props: React.SVGProps<SVGSVGElement>) => <Home {...props} />,
    variant: 'white',
    adminOnly: false,
    requiresCrisis: true,
    // No requiredPermissions - available to all authenticated users
    // No requiredPermissions - available to all authenticated users
  },
  {
    id: 'scans',
    titleKey: 'scan.titel',
    to: '/scan',
    icon: (props: React.SVGProps<SVGSVGElement>) => <Search {...props} />,
    variant: 'white',
    adminOnly: false,
    requiresCrisis: true,
    requiredPermissions: ['scans.list'], // Users need list permission to access page
  },
  {
    id: 'bob-analyse',
    titleKey: 'bob.titel',
    to: '/bob',
    icon: (props: React.SVGProps<SVGSVGElement>) => <Briefcase {...props} />,
    variant: 'white',
    adminOnly: false,
    requiresCrisis: true,
    requiredPermissions: ['bob.list'], // Users need list permission to access page
  },
  {
    id: 'writing',
    titleKey: 'writing.titel',
    to: '/writing',
    icon: (props: React.SVGProps<SVGSVGElement>) => <PenTool {...props} />,
    variant: 'white',
    adminOnly: false,
    requiresCrisis: true,
    requiredPermissions: ['writing.list'], // Users need list permission to access page
  },
  {
    id: 'contacts',
    titleKey: 'contacts.titel',
    to: '/contacts',
    icon: (props: React.SVGProps<SVGSVGElement>) => <Users {...props} />,
    variant: 'white',
    adminOnly: false,
    requiresCrisis: true,
    requiredPermissions: ['contacts.list'], // Users need list permission to access page
  },
  {
    id: 'planvorming',
    titleKey: 'planvorming.titel',
    to: '/planvorming',
    icon: (props: React.SVGProps<SVGSVGElement>) => <BookOpen {...props} />,
    variant: 'white',
    adminOnly: false,
    requiresCrisis: true,
    requiredPermissions: ['documents.browse'],
  },
  {
    id: 'crisis-select',
    titleKey: 'crisis.selectTitle',
    to: '/crisis/select',
    icon: (props: React.SVGProps<SVGSVGElement>) => <AlertTriangle {...props} />,
    variant: 'white',
    adminOnly: false,
    requiresCrisis: false,
    requiredPermissions: ['crisis.list'], // Users need list permission to access page
  },
]

// Bottom navigation items that are always visible (based on user role)
export const BOTTOM_NAV_ITEMS: NavItem[] = [
  {
    id: 'admin',
    titleKey: 'admin.titel',
    to: '/admin',
    icon: (props: React.SVGProps<SVGSVGElement>) => <Settings {...props} />,
    variant: 'white',
    adminOnly: true, // Kept for backward compatibility
    requiresCrisis: false,
    alwaysShow: true,
    requiredPermissions: ['users.view', 'rbac.roles.view'], // Can view users OR roles (admin access)
  },
  {
    id: 'profile',
    titleKey: 'navigatie.profiel',
    to: '/profile',
    icon: (props: React.SVGProps<SVGSVGElement>) => <User {...props} />,
    variant: 'white',
    adminOnly: false,
    requiresCrisis: false,
    alwaysShow: true,
    // No requiredPermissions - available to all authenticated users
    // No requiredPermissions - available to all authenticated users
  },
  {
    id: 'logout',
    titleKey: 'algemeen.uitloggen',
    to: '/logout',
    icon: (props: React.SVGProps<SVGSVGElement>) => <LogOut {...props} />,
    variant: 'white',
    adminOnly: false,
    requiresCrisis: false,
    alwaysShow: true,
    // No requiredPermissions - available to all authenticated users
    // No requiredPermissions - available to all authenticated users
  },
  {
    id: 'login',
    titleKey: 'algemeen.inloggen',
    to: '/login',
    icon: (props: React.SVGProps<SVGSVGElement>) => <LogIn {...props} />,
    variant: 'white',
    adminOnly: false,
    requiresCrisis: false,
    alwaysShow: true,
    // No requiredPermissions - available to unauthenticated users
    // No requiredPermissions - available to unauthenticated users
  },
]

export default NAV_ITEMS
